import React from 'react';
import { Package, Plus, ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Badge } from '../../../../components/ui/Badge';
import { PlansFiltersBar } from '../../../../components/filters/PlansFiltersBar';

export const PlansTable = ({
  //Plans
  products,
  //Pagination
  totalItems,
  totalPages,
  page,
  setPage,
  pageSize,
  setPageSize,
  pageSizeOptions,
  rangeStart,
  rangeEnd,
  //Filters
  searchQuery,
  setSearchQuery,
  filterAsset,
  setFilterAsset,
  filterPriority,
  setFilterPriority,
  filterMaintenanceType,
  setFilterMaintenanceType,
  uniqueAssets,
  uniquePriorities,
  uniqueMaintenanceTypes,
  //Handlers
  onProductClick,
  onCreate,
  selectedProductId
}) => {

  return (
    <div className="rounded-2xl border border-gray-700 bg-gray-800 shadow-lg w-full min-w-0 min-h-[420px] flex flex-col">
      <div className="px-4 sm:px-6 py-4 border-b border-gray-700 shrink-0">
        {/* Header with title and badge */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3 flex-wrap">
            <Package className="w-5 h-5 text-gray-400 shrink-0" />
            <h2 className="text-xl font-semibold text-white">
              Maintenance Plans List
            </h2>
            <Badge className="bg-blue-900/50 text-blue-200">
              {totalItems} {totalItems === 1 ? 'plan' : 'plans'}
            </Badge>
          </div>
          { /* Create button */}
          <button
            type="button"
            onClick={onCreate}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-green-600 bg-green-600 text-white text-sm font-medium hover:bg-green-700 hover:border-green-700 transition-colors cursor-pointer"
          >
            <Plus className="w-4 h-4" />
            Create Plan
          </button>
        </div>
        {/* Filters section below title */}
        <PlansFiltersBar
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          filterAsset={filterAsset}
          setFilterAsset={setFilterAsset}
          filterPriority={filterPriority}
          setFilterPriority={setFilterPriority}
          filterMaintenanceType={filterMaintenanceType}
          setFilterMaintenanceType={setFilterMaintenanceType}
          uniqueAssets={uniqueAssets}
          uniquePriorities={uniquePriorities}
          uniqueMaintenanceTypes={uniqueMaintenanceTypes}
        />
      </div>

      <div className="p-4 sm:p-6 w-full min-w-0 flex-1 flex flex-col">
        {products.length === 0 && totalItems === 0 ? (
          <div className="text-center py-16">
            <Package className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 text-lg">
              No plans registered.
            </p>
          </div>
        ) : products.length === 0 && totalItems > 0 ? (
          <div className="text-center py-16">
            <Search className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 text-lg">
              No plans found matching your search and filters.
            </p>
          </div>
        ) : (
          <>
            <div className="w-full flex-1 min-h-0 overflow-y-auto">
              {/* Vista de Cards para móvil */}
              <div className="grid grid-cols-1 gap-4 sm:hidden">
                {products.map((product, index) => (
                  <div
                    key={product.id}
                    onClick={() => onProductClick(product)}
                    className={`bg-gray-800 border border-gray-700 rounded-lg p-4 shadow-sm hover:bg-gray-750 transition-colors cursor-pointer ${selectedProductId === product.id ? 'ring-2 ring-blue-500 ring-inset' : ''
                      }`}
                  >
                    <h3 className="text-lg font-semibold text-white mb-3 text-center">
                      {product.name || 'N/A'}
                    </h3>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-400 font-medium min-w-[100px]">Asset:</span>
                        <Badge className="bg-violet-900/50 text-violet-200">
                          {product.asset_location || 'N/A'}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-400 font-medium min-w-[100px]">Maintenance:</span>
                        <span className="text-sm text-gray-300">{product.maintenance_type.charAt(0).toUpperCase() + product.maintenance_type.slice(1) || 'N/A'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-400 font-medium min-w-[100px]">Priority:</span>
                        <Badge className={product.priority.toLowerCase() === 'critical' ? 'bg-red-900/50 text-red-200' : product.priority.toLowerCase() === 'high' ? 'bg-orange-900/50 text-orange-200' : product.priority.toLowerCase() === 'medium' ? 'bg-yellow-900/50 text-yellow-200' : 'bg-green-900/50 text-green-200'}>
                          {product.priority.charAt(0).toUpperCase() + product.priority.slice(1) || 'N/A'}
                        </Badge>
                      </div>
                      {product.description && (
                        <div className="flex items-start gap-2 pt-1">
                          <span className="text-xs text-gray-400 font-medium min-w-[100px]">Description:</span>
                          <span className="text-sm text-gray-300 flex-1">{product.description}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Vista de tabla para desktop */}
              <div className="hidden sm:block w-full">
                <div className="inline-block w-full align-middle">
                  <div className="shadow ring-1 ring-gray-700 rounded-lg w-full">
                    <table className="w-full divide-y divide-gray-700">
                      {/* Vista de tabla para desktop - oculta en móvil */}
                      <thead className="sticky top-0 z-0 bg-gray-700/50 shadow-sm hidden sm:table-header-group">
                        <tr>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                            Name
                          </th>
                          <th className="px-6 py-4 text-center text-xs font-semibold text-gray-300 uppercase tracking-wider">
                            Asset
                          </th>
                          <th className="px-6 py-4 text-center text-xs font-semibold text-gray-300 uppercase tracking-wider">
                            Maintenance
                          </th>
                          <th className="px-6 py-4 text-center text-xs font-semibold text-gray-300 uppercase tracking-wider">
                            Priority
                          </th>
                          <th className="px-6 py-4 text-right text-xs font-semibold text-gray-300 uppercase tracking-wider">
                            Description
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-gray-800 divide-y divide-gray-700 hidden sm:table-row-group">
                        {products.map((product, index) => (
                          <tr
                            key={product.id}
                            onClick={() => onProductClick(product)}
                            className={`transition-colors cursor-pointer ${index % 2 === 0 ? 'bg-gray-800' : 'bg-gray-750'
                              } hover:bg-gray-700 ${selectedProductId === product.id ? 'ring-2 ring-blue-500 ring-inset' : ''
                              }`}
                          >
                            <td className="px-6 py-4 whitespace-nowrap text-left">
                              <span className="text-sm font-medium text-white">
                                {product.name || 'N/A'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              <div className="flex justify-center">
                                <Badge className="bg-violet-900/50 text-violet-200">
                                  {product?.asset_location || 'N/A'}
                                </Badge>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300 text-center">
                              {product.maintenance_type.charAt(0).toUpperCase() + product.maintenance_type.slice(1) || 'N/A'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300 text-center">
                              <Badge className={product.priority.toLowerCase() === 'critical' ? 'bg-red-900/50 text-red-200' : product.priority.toLowerCase() === 'high' ? 'bg-orange-900/50 text-orange-200' : product.priority.toLowerCase() === 'medium' ? 'bg-yellow-900/50 text-yellow-200' : 'bg-green-900/50 text-green-200'}>
                                {product.priority.charAt(0).toUpperCase() + product.priority.slice(1) || 'N/A'}
                              </Badge>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-300 text-right">
                              <div className="max-w-xs mx-auto">
                                <div className="truncate" title={product.description || 'N/A'}>
                                  {product.description || 'N/A'}
                                </div>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            {totalItems > 0 && (
              <div className="px-4 sm:px-6 py-4 border-t border-gray-700 flex flex-col sm:flex-row items-center justify-between gap-4 shrink-0">
                <div className="flex items-center gap-4 flex-wrap">
                  <p className="text-sm text-gray-500">
                    Mostrando {rangeStart}–{rangeEnd} de {totalItems} planes
                  </p>
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-400">Mostrar:</label>
                    <select
                      value={pageSize}
                      onChange={(e) => setPageSize(Number(e.target.value))}
                      className="px-3 py-1.5 rounded-lg border border-gray-600 bg-gray-800 text-gray-300 text-sm font-medium hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      {pageSizeOptions?.map((size) => (
                        <option key={size} value={size} className="bg-gray-800">
                          {size}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-gray-600 bg-gray-800 text-gray-300 text-sm font-medium hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Anterior
                  </button>
                  <span className="text-sm text-gray-500 px-2">
                    Página {page} de {totalPages}
                  </span>
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                    className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-gray-600 bg-gray-800 text-gray-300 text-sm font-medium hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Siguiente
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};